import React, { useCallback, useEffect, useRef } from "react";
import { appendSampledPoint, crayonWidths, normalizeClientPoint } from "./writingStroke";
import { LEARN } from "../theme";

/**
 * Retina pointer-event sketch canvas. Stores normalized 0…1 strokes.
 * Sampling matches iOS: append only when movement >= 1.5px.
 *
 * Draws, back to front: kindergarten paper lines (top / dashed mid /
 * baseline), the faded reference guide with numbered start dots, then the
 * child's crayon ink (round caps, width eases with speed) and a pencil-tip
 * ring on the live stroke.
 */
const WritingSketchCanvas = ({
  strokes,
  onChange,
  onStrokeEnded,
  onStrokeStarted,
  width,
  height,
  disabled = false,
  guideStrokes = [],
  accent = LEARN.color.lavender,
  inkColor,
  showGuide = true,
  paper = true,
  guideLines = null,       // { top, mid, base } normalized y; null → defaults
  guideProgress = null,
  guideWidth = null,       // px band width; null → derived from canvas height
  showStartDots = true,
  inkWidth = null,         // px; null → derived from canvas height
  style,
}) => {
  const canvasRef = useRef(null);
  const activeRef = useRef([]);
  const drawingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;

  const toNorm = useCallback((clientX, clientY) => {
    return normalizeClientPoint(clientX, clientY, canvasRef.current?.getBoundingClientRect());
  }, []);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return;
    const dpr = Math.max(1, Math.min(3, (typeof window !== "undefined" && window.devicePixelRatio) || 1));
    const w = Math.max(1, width || canvas.clientWidth);
    const h = Math.max(1, height || canvas.clientHeight);
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const viewport = { width: w, height: h };
    const ink = inkColor || accent;
    const baseInk = inkWidth || Math.max(7, Math.min(16, h * 0.028));

    if (paper) {
      const lines = guideLines || { top: 0.16, mid: 0.46, base: 0.8 };
      const inset = Math.max(14, w * 0.04);
      const drawLine = (frac, color, lineWidth, dash) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.setLineDash(dash || []);
        ctx.beginPath();
        ctx.moveTo(inset, Math.round(h * frac) + 0.5);
        ctx.lineTo(w - inset, Math.round(h * frac) + 0.5);
        ctx.stroke();
      };
      // Kindergarten paper: faint ink headline, dashed midline in the accent
      // at 35%, a slightly firmer accent baseline to sit the letters on.
      drawLine(lines.top, "rgba(11,26,51,0.11)", 1.5);
      drawLine(lines.mid, hexAlpha(accent, 0.35), 2, [7, 8]);
      drawLine(lines.base, hexAlpha(accent, 0.5), 2.5);
      ctx.setLineDash([]);
    }

    if (showGuide && guideStrokes.length) {
      const band = guideWidth || Math.max(16, Math.min(34, h * 0.062));
      guideStrokes.forEach((stroke) => {
        drawPolyline(ctx, stroke.points, w, h, hexAlpha(accent, 0.13), band, stroke.isClosed);
        if (stroke.kind === "dot") {
          fillPolygon(ctx, stroke.points, w, h, hexAlpha(accent, 0.5));
        } else {
          drawPolyline(ctx, stroke.points, w, h, hexAlpha(accent, 0.5), 3, stroke.isClosed, [5, 9]);
        }
      });
      if (guideProgress != null) {
        trimGuideStrokes(guideStrokes, guideProgress).forEach((stroke) => {
          drawPolyline(ctx, stroke.points, w, h, accent, band * 0.55, false);
        });
      }
      if (showStartDots) {
        let number = 0;
        guideStrokes.forEach((stroke) => {
          if (stroke.kind === "dot" || !stroke.points?.length) return;
          number += 1;
          const start = stroke.start || stroke.points[0];
          drawStartDot(ctx, start.x * w, start.y * h, number, accent, Math.max(11, Math.min(15, h * 0.03)));
        });
      }
    }

    const visible = [...(strokesRef.current || [])];
    if (activeRef.current.length) visible.push({ points: activeRef.current, live: true });
    visible.forEach((stroke) => {
      const widths = crayonWidths(stroke.points, viewport, baseInk);
      drawCrayon(ctx, stroke.points, widths, w, h, hexAlpha(ink, 0.14), 1.7);
      drawCrayon(ctx, stroke.points, widths, w, h, ink, 1);
    });

    const tip = activeRef.current[activeRef.current.length - 1];
    if (tip) {
      ctx.beginPath();
      ctx.arc(tip.x * w, tip.y * h, baseInk * 0.95, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = ink;
      ctx.stroke();
    }
  }, [accent, guideLines, guideProgress, guideStrokes, guideWidth, height, inkColor, inkWidth, paper, showGuide, showStartDots, width]);

  useEffect(() => {
    paint();
  }, [paint, strokes]);

  const onPointerDown = (event) => {
    if (disabled) return;
    if (event.button != null && event.button !== 0) return;
    if (drawingRef.current) return;
    const point = toNorm(event.clientX, event.clientY);
    if (!point) return;
    event.preventDefault?.();
    pointerIdRef.current = event.pointerId;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // capture is best-effort (some synthetic events have no pointer id)
    }
    drawingRef.current = true;
    activeRef.current = [point];
    onStrokeStarted?.();
    paint();
  };

  const onPointerMove = (event) => {
    if (!drawingRef.current || disabled) return;
    if (pointerIdRef.current != null && event.pointerId != null && event.pointerId !== pointerIdRef.current) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const events = typeof event.nativeEvent?.getCoalescedEvents === "function"
      ? event.nativeEvent.getCoalescedEvents()
      : null;
    const samples = events && events.length ? events : [event];
    let next = activeRef.current;
    for (const sample of samples) {
      const point = toNorm(sample.clientX, sample.clientY);
      if (point) next = appendSampledPoint(next, point, 1.5, rect);
    }
    activeRef.current = next;
    paint();
  };

  const endStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    pointerIdRef.current = null;
    const points = activeRef.current;
    activeRef.current = [];
    if (points.length >= 2) {
      const next = [...(strokesRef.current || []), { points }];
      onChange?.(next);
      onStrokeEnded?.(next);
    } else if (points.length === 1) {
      // A tap: keep it as a tiny dot so "i" and "j" dots count.
      const p = points[0];
      const dotPoints = [p, { x: p.x + 0.002, y: p.y + 0.002 }];
      const next = [...(strokesRef.current || []), { points: dotPoints }];
      onChange?.(next);
      onStrokeEnded?.(next);
    }
    paint();
  };

  return (
    <canvas
      ref={canvasRef}
      data-testid="writing-sketch-canvas"
      width={width}
      height={height}
      role="img"
      aria-label={disabled ? "Writing paper" : "Writing paper. Draw with your finger or mouse."}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endStroke}
      onPointerCancel={endStroke}
      onLostPointerCapture={endStroke}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        width: "100%",
        height: "100%",
        touchAction: "none",
        display: "block",
        cursor: disabled ? "default" : "crosshair",
        WebkitUserSelect: "none",
        userSelect: "none",
        ...style,
      }}
    />
  );
};

function trimGuideStrokes(strokes, progress) {
  if (progress == null) return [];
  const scaled = Math.min(1, Math.max(0, progress)) * strokes.length;
  return strokes
    .map((stroke, index) => {
      const local = Math.min(1, Math.max(0, scaled - index));
      if (local <= 0) return null;
      return { points: trimPoints(stroke.points, local) };
    })
    .filter(Boolean);
}

export function trimPoints(points, fraction) {
  if (!points?.length || fraction >= 1) return points || [];
  if (fraction <= 0 || points.length === 1) return points.slice(0, 1);
  let total = 0;
  const segments = [];
  for (let i = 1; i < points.length; i += 1) {
    const length = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    segments.push(length);
    total += length;
  }
  let remain = total * fraction;
  const out = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const length = segments[i - 1];
    if (remain >= length) {
      out.push(points[i]);
      remain -= length;
      continue;
    }
    const t = length ? remain / length : 1;
    out.push({
      x: points[i - 1].x + (points[i].x - points[i - 1].x) * t,
      y: points[i - 1].y + (points[i].y - points[i - 1].y) * t,
    });
    break;
  }
  return out;
}

function drawPolyline(ctx, points, w, h, color, width, closed, dash) {
  if (!points?.length) return;
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.setLineDash(dash || []);
  ctx.moveTo(points[0].x * w, points[0].y * h);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x * w, points[i].y * h);
  }
  if (closed) ctx.closePath();
  ctx.stroke();
  ctx.setLineDash([]);
}

function fillPolygon(ctx, points, w, h, color) {
  if (!points?.length) return;
  ctx.beginPath();
  ctx.fillStyle = color;
  ctx.moveTo(points[0].x * w, points[0].y * h);
  for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i].x * w, points[i].y * h);
  ctx.closePath();
  ctx.fill();
}

/** Variable-width crayon: each segment stroked with its own width, round caps blend them. */
function drawCrayon(ctx, points, widths, w, h, color, widthScale) {
  if (!points?.length) return;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.setLineDash([]);
  if (points.length === 1) {
    ctx.beginPath();
    ctx.arc(points[0].x * w, points[0].y * h, (widths[0] * widthScale) / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  for (let i = 1; i < points.length; i += 1) {
    ctx.beginPath();
    ctx.lineWidth = ((widths[i - 1] + widths[i]) / 2) * widthScale;
    ctx.moveTo(points[i - 1].x * w, points[i - 1].y * h);
    ctx.lineTo(points[i].x * w, points[i].y * h);
    ctx.stroke();
  }
}

function drawStartDot(ctx, x, y, number, accent, radius) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = accent;
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = "#fff";
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.font = `700 ${Math.round(radius * 1.35)}px ${LEARN.font}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(number), x, y + 0.5);
}

export function hexAlpha(hex, alpha) {
  const raw = String(hex || "#946EF2").replace("#", "");
  if (raw.startsWith("rgb")) return raw;
  const n = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default WritingSketchCanvas;
